/**
 * Chip de status no topo (spec 015, US1): Disponível, Em pausa (motivo) com o
 * contador correndo, ou Ausente. O menu troca o PRÓPRIO status; a equipe vê a
 * mudança pelo evento realtime. O status é da pessoa, não da aba — duas abas
 * mostram o mesmo intervalo, e escolher de novo o status atual não zera nada
 * (a API trata como no-op).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Coffee, LogOut, Pause, Play } from 'lucide-react';
import { ATTENDANT_STATUS_LABELS, type AttendantStatus, type Presence } from '@erp/shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTransientToast } from '@/components/ui/transient-toast';
import { cn } from '@/lib/utils';
import { useSetMyPresence } from './presence-mutations';
import { elapsedLabel, myPresenceQuery, pauseReasonsQuery, presenceErrorMessage } from './presence-queries';

export const STATUS_DOT: Record<AttendantStatus, string> = {
  available: 'bg-[#52C41A]',
  paused: 'bg-amber-500',
  away: 'bg-muted-foreground/50',
};

/** "Em pausa · Almoço" / "Disponível" / "Ausente" */
export function presenceLabel(p: Pick<Presence, 'status' | 'reasonName'> | undefined): string {
  if (!p) return ATTENDANT_STATUS_LABELS.away;
  if (p.status === 'paused' && p.reasonName) return `${ATTENDANT_STATUS_LABELS.paused} · ${p.reasonName}`;
  return ATTENDANT_STATUS_LABELS[p.status];
}

/** relógio que só anda enquanto `active` — o chip disponível não re-renderiza à toa */
export function useNow(active: boolean, everyMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [active, everyMs]);
  return now;
}

export function PresenceChip() {
  const meQ = useQuery(myPresenceQuery());
  const reasonsQ = useQuery(pauseReasonsQuery({ activeOnly: true }));
  const setStatus = useSetMyPresence();
  // o menu fecha no clique: o erro vai para um toast, não para dentro dele
  const { toast, showToast } = useTransientToast('error');

  const me = meQ.data;
  const status: AttendantStatus = me?.status ?? 'away';
  const paused = status === 'paused';
  const now = useNow(paused);

  const change = (body: { status: AttendantStatus; reasonId?: number }) => {
    setStatus.mutate(body, { onError: (e) => showToast(presenceErrorMessage(e)) });
  };

  return (
    <>
    {toast}
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Seu status: ${presenceLabel(me)}`}
          disabled={meQ.isPending}
          className={cn(
            'flex h-8 max-w-60 items-center gap-2 rounded-full border px-3 text-[13px] font-medium outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/50',
            paused && 'border-amber-500/60 bg-amber-500/10'
          )}
        >
          <span className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status])} />
          <span className="truncate">{presenceLabel(me)}</span>
          {paused && me?.since ? (
            <span className="shrink-0 tabular-nums text-muted-foreground">{elapsedLabel(me.since, now)}</span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Seu status</DropdownMenuLabel>
        <DropdownMenuItem
          disabled={setStatus.isPending || status === 'available'}
          onSelect={() => change({ status: 'available' })}
        >
          <Play />
          {paused ? 'Voltar da pausa' : 'Disponível'}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={setStatus.isPending}>
            <Pause />
            {paused ? 'Trocar o motivo da pausa' : 'Pausa'}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-52">
            {(reasonsQ.data ?? []).length === 0 ? (
              <DropdownMenuItem disabled>Nenhum motivo ativo</DropdownMenuItem>
            ) : (
              (reasonsQ.data ?? []).map((r) => (
                <DropdownMenuItem
                  key={r.id}
                  disabled={paused && me?.reasonId === r.id}
                  onSelect={() => change({ status: 'paused', reasonId: r.id })}
                >
                  <Coffee />
                  {r.name}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem
          disabled={setStatus.isPending || status === 'away'}
          onSelect={() => change({ status: 'away' })}
        >
          <LogOut />
          Ausente (fim do expediente)
        </DropdownMenuItem>
        {paused ? (
          <>
            <DropdownMenuSeparator />
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              Em pausa você não assume nem recebe conversas.
            </p>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}
